import type { ReferencedEntity } from "@cohesive/api-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import {
  buildFileRecordTextSelectionEntitiesFromCache,
  fileRecordTextSelectionEntityId,
  fileRecordTextSelectionQueryKey,
  isTextSelectionEntityId,
  normalizeFileRecordTextSelectionCache,
  textSelectionEntityIdsFromReferencedEntities,
  type FileRecordTextSelectionCache,
} from "./fileRecordTextSelection";

export function useFileRecordTextSelectionReferencedEntity(scopeId: string) {
  const queryClient = useQueryClient();

  const { data: selectionCacheRaw = [] } = useQuery<
    FileRecordTextSelectionCache[]
  >({
    queryKey: fileRecordTextSelectionQueryKey(scopeId),
    queryFn: () => [],
    enabled: !!scopeId,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const selectionCache = useMemo(
    () => normalizeFileRecordTextSelectionCache(selectionCacheRaw),
    [selectionCacheRaw],
  );

  const textSelectionEntities: ReferencedEntity[] = useMemo(
    () => buildFileRecordTextSelectionEntitiesFromCache(selectionCache),
    [selectionCache],
  );

  const removeSelection = useCallback(
    (entityId: string) => {
      queryClient.setQueryData<FileRecordTextSelectionCache[]>(
        fileRecordTextSelectionQueryKey(scopeId),
        (prev) =>
          normalizeFileRecordTextSelectionCache(prev).filter(
            (entry) => fileRecordTextSelectionEntityId(entry) !== entityId,
          ),
      );
    },
    [scopeId, queryClient],
  );

  const clearSelectionsFromMessage = useCallback(
    (sentReferencedEntities: ReferencedEntity[] | undefined) => {
      const idsToRemove = new Set(
        textSelectionEntityIdsFromReferencedEntities(sentReferencedEntities),
      );
      if (idsToRemove.size === 0) return;

      queryClient.setQueryData<FileRecordTextSelectionCache[]>(
        fileRecordTextSelectionQueryKey(scopeId),
        (prev) =>
          normalizeFileRecordTextSelectionCache(prev).filter(
            (entry) => !idsToRemove.has(fileRecordTextSelectionEntityId(entry)),
          ),
      );
    },
    [scopeId, queryClient],
  );

  return {
    textSelectionEntities,
    removeSelection,
    clearSelectionsFromMessage,
    isTextSelectionEntityId,
  };
}
